// src/lib/opportunity/opportunityGeneratedClient.ts
//
// Browser-side helpers for the generated-PDF flow on the Opportunity
// Engine. Pattern matches `opportunityDocumentsClient`: sign → upload →
// commit, plus a list endpoint that returns signed read URLs (30 min TTL).

import { opportunityFetch } from "@/lib/opportunity/opportunityFetch";
import { requireSupabase } from "@/lib/supabase";
import type { OpportunityOutputType } from "@/types/opportunity";

const SIGN_PATH = "/api/opportunity/generated-sign";
const COMMIT_PATH = "/api/opportunity/generated-commit";
const LIST_PATH = "/api/opportunity/generated-list";
const DELETE_PATH = "/api/opportunity/generated-delete";

export type OpportunityGeneratedOutput = {
  id: string;
  organization_id: string;
  opportunity_id: string;
  revision_id: string | null;
  output_type: OpportunityOutputType;
  alternate_key: string | null;
  alternate_label: string | null;
  storage_bucket: string;
  object_path: string;
  byte_size: number;
  generated_by_user_id: string | null;
  generated_at: string;
  signed_url?: string;
  signed_expires_at?: string;
  generated_by_name?: string | null;
  pricing_summary?: {
    total_sale_price: number;
    deposit_amount: number;
  } | null;
};

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  output_id: string;
  output_type: OpportunityOutputType;
};

type CommitOk = {
  ok: true;
  output: OpportunityGeneratedOutput;
};

export type OpportunityGeneratedListResponse = {
  ok: true;
  outputs: OpportunityGeneratedOutput[];
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.output_id === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && typeof o.output === "object" && o.output !== null;
}

export type UploadGeneratedPdfResult =
  | { ok: true; output: OpportunityGeneratedOutput }
  | { ok: false; error: string };

export async function uploadGeneratedProposalPdf(args: {
  opportunityId: string;
  revisionId: string | null;
  outputType: OpportunityOutputType;
  blob: Blob;
  alternateKey?: string | null;
  alternateLabel?: string | null;
}): Promise<UploadGeneratedPdfResult> {
  const sb = requireSupabase();
  const byteSize = args.blob.size;
  if (byteSize <= 0) {
    return { ok: false, error: "Generated PDF is empty — nothing to upload." };
  }

  const sign = await opportunityFetch<unknown>(SIGN_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      opportunity_id: args.opportunityId,
      revision_id: args.revisionId,
      output_type: args.outputType,
      content_type: "application/pdf",
      byte_size: byteSize,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const err = (sign.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Sign response invalid" };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage
    .from(s.bucket)
    .uploadToSignedUrl(s.path, s.token, args.blob, {
      contentType: "application/pdf",
      upsert: false,
    });
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await opportunityFetch<unknown>(COMMIT_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      opportunity_id: args.opportunityId,
      output_id: s.output_id,
      object_path: s.path,
      revision_id: args.revisionId,
      output_type: args.outputType,
      byte_size: byteSize,
      alternate_key: args.alternateKey?.trim() || null,
      alternate_label: args.alternateLabel?.trim() || null,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const err = (commit.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Commit response invalid" };
  }
  return { ok: true, output: commit.data.output };
}

export function listOpportunityGeneratedOutputs(args: {
  opportunityId: string;
  signMedia?: boolean;
}) {
  const q = new URLSearchParams();
  q.set("opportunity_id", args.opportunityId);
  if (args.signMedia) q.set("sign_media", "1");
  return opportunityFetch<OpportunityGeneratedListResponse>(`${LIST_PATH}?${q.toString()}`, {
    method: "GET",
  });
}

export function deleteOpportunityGeneratedOutput(outputId: string) {
  return opportunityFetch<{ ok: true }>(DELETE_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ output_id: outputId }),
  });
}
